import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import WeeklyPreview from "../components/WeeklyPreview";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Sparkles, ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle,
  Heart, MapPin, Car, CalendarCheck,
  UserCheck, ClipboardList, GraduationCap,
} from "lucide-react";
import { nextMonday } from "../lib/dates";

const DISCIPLINES = [
  { key: "bt",              label: "BT",   long: "Behavior Therapist",  icon: UserCheck,     defaultHours: 10,   description: "Primary direct-care therapist, 10 hr/week" },
  { key: "program_manager", label: "PM",   long: "Program Manager",      icon: ClipboardList, defaultHours: 2,    description: "Observes & supervises during BT sessions, 2 hr/week" },
  { key: "bcba",            label: "BCBA", long: "BCBA",                  icon: GraduationCap, defaultHours: 0.75, description: "Senior clinical oversight, ~3 hr/month" },
];

const palette = ["#274f38", "#B07C60", "#7B968B"];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function buildDefaultDailyHours(totalHours) {
  const result = {};
  let remaining = totalHours;
  for (let day = 0; day < 5 && remaining > 0; day++) {
    const hrs = Math.min(2, remaining);
    if (hrs > 0) result[day] = hrs;
    remaining = Math.round((remaining - hrs) * 100) / 100;
  }
  return result;
}

// ---- Coverage bar ----
function CoverageBar({ covered, needed }) {
  const pct = needed > 0 ? Math.min(100, Math.round((covered / needed) * 100)) : 0;
  const full = covered >= needed - 0.01;
  return (
    <div data-testid="coverage-bar">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] text-[#586960] uppercase tracking-[0.15em] font-semibold">Coverage</span>
        <span className="text-[11px] font-mono text-[#18231E]">
          {covered.toFixed(2)}<span className="text-[#A1ACA6]"> / {needed} hr</span>
          <span className={`ml-1.5 font-semibold ${full ? "text-[#274f38]" : "text-[#B07C60]"}`}>{pct}%</span>
        </span>
      </div>
      <div className="w-full h-1.5 bg-[#F0EFEA] rounded-full overflow-hidden">
        <div
          style={{ width: `${pct}%`, backgroundColor: full ? "#274f38" : "#B07C60" }}
          className="h-full rounded-full transition-all duration-500"
        />
      </div>
    </div>
  );
}

// ---- Therapist option card ----
function OptionCard({ option, idx, isSelected, onSelect, color, testId }) {
  const t = option.therapists[0];
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onSelect}
      className={`group text-left w-full rounded-xl transition-all duration-200 overflow-hidden ${
        isSelected
          ? "ring-2 ring-[#274f38] shadow-[0_8px_24px_rgba(39,79,56,0.12)]"
          : "shadow-[0_2px_8px_rgba(24,35,30,0.04)] hover:shadow-[0_8px_24px_rgba(24,35,30,0.07)] border border-[#E3DFD5]"
      }`}
    >
      {/* Colored top strip */}
      <div className="h-1 w-full" style={{ backgroundColor: color }} />

      <div className={`bg-white p-4 ${isSelected ? "bg-[#F7FAF8]" : ""}`}>
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            {/* Rank badge */}
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0"
              style={{ backgroundColor: color }}
            >
              {idx + 1}
            </div>

            <div>
              <div className="font-semibold text-[#18231E] text-[15px] leading-tight" style={{ fontFamily: "Outfit" }}>
                {t.therapist_name}
              </div>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-[11px] text-[#586960] capitalize bg-[#F0EFEA] px-2 py-0.5 rounded-full">
                  {t.skill_level}
                </span>
                {t.drive_minutes != null && (
                  <span className="text-[11px] text-[#586960] inline-flex items-center gap-1">
                    <Car size={10} strokeWidth={1.5} />
                    <span className="font-mono">{t.drive_minutes.toFixed(0)} min</span>
                  </span>
                )}
                {t.is_existing_relationship && (
                  <span className="text-[11px] text-[#B07C60] inline-flex items-center gap-1 bg-[#FDF4E7] px-2 py-0.5 rounded-full">
                    <Heart size={10} strokeWidth={1.5} /> existing
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Score */}
          <div className="text-center shrink-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1ACA6] font-semibold mb-0.5">Score</div>
            <div className="text-3xl font-medium leading-none" style={{ color, fontFamily: "Outfit" }}>
              {option.score}
            </div>
          </div>
        </div>

        {/* Coverage bar */}
        <CoverageBar covered={option.coverage_hours} needed={option.needed_hours} />

        {/* Status chips */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {option.fully_covered ? (
            <span className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full bg-[#E8F0EA] text-[#274f38] font-semibold uppercase tracking-[0.12em]">
              <CheckCircle2 size={10} /> Full coverage
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full bg-[#FDF4E7] text-[#B07C60] font-semibold uppercase tracking-[0.12em]">
              <AlertTriangle size={10} /> {option.gap_hours.toFixed(2)} hr gap
            </span>
          )}
          {isSelected && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full bg-[#274f38] text-white font-semibold uppercase tracking-[0.12em]">
              <CheckCircle2 size={10} /> Selected
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ---- Step progress indicator ----
function StepProgress({ step }) {
  const steps = [
    { idx: 0, label: "Client" },
    { idx: 1, label: "BT" },
    { idx: 2, label: "Prog. Mgr" },
    { idx: 3, label: "BCBA" },
    { idx: 4, label: "Review" },
  ];

  return (
    <div className="px-10 pt-2 pb-4" data-testid="wizard-stepper">
      <div className="bg-white border border-[#E3DFD5] rounded-xl p-5 shadow-[0_2px_8px_rgba(24,35,30,0.04)]">
        <div className="flex items-center">
          {steps.map((s, i) => {
            const isActive = step === s.idx;
            const isComplete = step > s.idx;
            return (
              <React.Fragment key={s.idx}>
                <div className="flex flex-col items-center gap-1.5 min-w-[56px]">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-300 ${
                      isComplete
                        ? "bg-[#274f38] border-[#274f38] text-white"
                        : isActive
                        ? "bg-white border-[#274f38] text-[#274f38] shadow-[0_0_0_3px_rgba(39,79,56,0.12)]"
                        : "bg-white border-[#E3DFD5] text-[#A1ACA6]"
                    }`}
                  >
                    {isComplete ? <CheckCircle2 size={14} strokeWidth={2.5} /> : s.idx + 1}
                  </div>
                  <span
                    className={`text-[10px] uppercase tracking-[0.15em] font-semibold whitespace-nowrap ${
                      isActive || isComplete ? "text-[#274f38]" : "text-[#A1ACA6]"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className="flex-1 mx-1 mb-5">
                    <div className="h-0.5 w-full rounded-full overflow-hidden bg-[#F0EFEA]">
                      <div
                        className="h-full bg-[#274f38] transition-all duration-500"
                        style={{ width: step > s.idx ? "100%" : step === s.idx ? "50%" : "0%" }}
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---- Discipline icon chip ----
function DisciplineChip({ discipline, picked }) {
  const d = DISCIPLINES.find(x => x.key === discipline);
  const opt = picked[discipline];
  const colorIdx = DISCIPLINES.findIndex(x => x.key === discipline);
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${
      opt ? "border-[#274f38] bg-[#E8F0EA] text-[#274f38]" : "border-[#E3DFD5] bg-white text-[#A1ACA6]"
    }`}>
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: opt ? palette[colorIdx] : "#E3DFD5" }} />
      {d.label}: {opt ? opt.therapists[0].therapist_name.split(" ")[0] : "—"}
    </div>
  );
}

export default function MatchingPage() {
  const [clients, setClients] = useState([]);
  const [step, setStep] = useState(0);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [client, setClient] = useState(null);
  const [teamHours, setTeamHours] = useState({ bt: 10, program_manager: 2, bcba: 0.75 });

  const [matches, setMatches] = useState({ bt: null, program_manager: null, bcba: null });
  const [picked, setPicked] = useState({ bt: null, program_manager: null, bcba: null });
  const [loadingDisc, setLoadingDisc] = useState(null);

  const [dailyHours, setDailyHours] = useState({});
  const [pmDailyHours, setPmDailyHours] = useState({});
  const [bcbaDays, setBcbaDays] = useState([]);

  const [weekStart, setWeekStart] = useState(nextMonday());
  const [recurringWeeks, setRecurringWeeks] = useState(26);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/clients");
      setClients(data);
    })();
  }, []);

  const startMatching = async () => {
    if (!selectedClientId) { toast.error("Pick a client first"); return; }
    const c = clients.find(x => x.id === selectedClientId);
    setClient(c);
    setMatches({ bt: null, program_manager: null, bcba: null });
    setPicked({ bt: null, program_manager: null, bcba: null });
    const ch = c.team_hours || {};
    const merged = { bt: ch.bt ?? 10, program_manager: ch.program_manager ?? 2, bcba: ch.bcba ?? 0.75 };
    setTeamHours(merged);
    setDailyHours(buildDefaultDailyHours(merged.bt));
    setStep(1);
  };

  const anchorBlocksFromBT = () => {
    const bt = picked.bt;
    if (!bt) return [];
    return bt.proposed_blocks.map(b => ({ day: b.day, start: b.start, end: b.end }));
  };

  const defaultPMDailyHours = (btBlocks) => {
    const byDay = {};
    btBlocks.forEach(b => {
      const [sh, sm] = b.start.split(":").map(Number);
      const [eh, em] = b.end.split(":").map(Number);
      byDay[b.day] = (byDay[b.day] || 0) + ((eh * 60 + em) - (sh * 60 + sm)) / 60;
    });
    const best = Object.entries(byDay).sort((a, b) => b[1] - a[1])[0];
    return best ? { [best[0]]: 2 } : {};
  };

  const loadMatch = async (discipline, target, anchorBlocks, daily) => {
    setLoadingDisc(discipline);
    try {
      const body = {
        client_id: selectedClientId,
        discipline,
        target_hours: target,
        anchor_blocks: anchorBlocks || [],
      };
      if (daily && Object.keys(daily).length > 0) {
        body.daily_hours = Object.fromEntries(
          Object.entries(daily).map(([d, h]) => [String(d), parseFloat(h)])
        );
      }
      const { data } = await api.post("/match/smart", body);
      setMatches((prev) => ({ ...prev, [discipline]: data }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoadingDisc(null);
    }
  };

  const advanceTo = async (newStep) => {
    setStep(newStep);
    if (newStep === 2 && picked.bt) {
      const pmDefault = defaultPMDailyHours(picked.bt.proposed_blocks);
      setPmDailyHours(pmDefault);
      if (!matches.program_manager) {
        await loadMatch("program_manager", teamHours.program_manager, anchorBlocksFromBT(), pmDefault);
      }
    } else if (newStep === 3 && picked.bt) {
      const btDaysSorted = [...new Set(picked.bt.proposed_blocks.map(b => b.day))].sort();
      const defaultDays = btDaysSorted.slice(0, 3);
      setBcbaDays(defaultDays);
      if (!matches.bcba) {
        const bcbaDaily = Object.fromEntries(defaultDays.map(d => [d, 1]));
        await loadMatch("bcba", defaultDays.length, anchorBlocksFromBT(), bcbaDaily);
      }
    }
  };

  const runBTMatch  = async () => loadMatch("bt", teamHours.bt, [], dailyHours);
  const runPMMatch  = async () => loadMatch("program_manager", teamHours.program_manager, anchorBlocksFromBT(), pmDailyHours);
  const runBCBAMatch = async (days) => {
    const bcbaDaily = Object.fromEntries(days.map(d => [d, 1]));
    await loadMatch("bcba", days.length, anchorBlocksFromBT(), bcbaDaily);
  };

  const pickOption = (discipline, option) => setPicked((prev) => ({ ...prev, [discipline]: option }));

  const allTherapists = useMemo(() => {
    const out = [];
    if (picked.bt)              out.push({ ...picked.bt.therapists[0],              discipline: "bt" });
    if (picked.program_manager) out.push({ ...picked.program_manager.therapists[0], discipline: "program_manager" });
    if (picked.bcba)            out.push({ ...picked.bcba.therapists[0],            discipline: "bcba" });
    return out;
  }, [picked]);

  const allBlocks = useMemo(() => {
    const out = [];
    DISCIPLINES.forEach((d) => { const opt = picked[d.key]; if (!opt) return; opt.proposed_blocks.forEach(b => out.push({ ...b })); });
    return out;
  }, [picked]);

  const allExisting = useMemo(() => {
    const out = [];
    DISCIPLINES.forEach((d) => { const opt = picked[d.key]; if (!opt) return; (opt.existing_blocks || []).forEach(b => out.push(b)); });
    return out;
  }, [picked]);

  const confirmTeam = async () => {
    if (!picked.bt) { toast.error("Pick at least the BT before confirming."); return; }
    setConfirming(true);
    try {
      const proposed = [];
      DISCIPLINES.forEach((d) => { const opt = picked[d.key]; if (!opt) return; opt.proposed_blocks.forEach(b => proposed.push(b)); });
      const { data } = await api.post("/match/confirm", {
        client_id: selectedClientId,
        week_start_date: weekStart,
        proposed_blocks: proposed,
        recurring_weeks: recurringWeeks,
        bcba_monthly: !!picked.bcba,
      });
      const skipped = data.skipped?.length || 0;
      toast.success(`Scheduled ${data.created} session${data.created !== 1 ? "s" : ""} across ${data.weeks_scheduled} weeks${skipped ? ` (${skipped} skipped)` : ""}`);
      setStep(0); setSelectedClientId(""); setClient(null);
      setPicked({ bt: null, program_manager: null, bcba: null });
      setMatches({ bt: null, program_manager: null, bcba: null });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F5]">
      <PageHeader
        title="Team matching"
        subtitle="Build a complete care team — BT first, then PM and BCBA who join the BT's sessions."
      />

      <StepProgress step={step} />

      <div className="px-10 pb-12 space-y-5">

        {/* ---- STEP 0: client selection ---- */}
        {step === 0 && (
          <div data-testid="step-client" className="max-w-3xl mx-auto space-y-5">
            {/* Client picker card */}
            <div className="bg-white border border-[#E3DFD5] rounded-xl p-7 shadow-[0_2px_8px_rgba(24,35,30,0.04)]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1ACA6] font-bold mb-1">Step 1 of 5</div>
              <h2 className="text-2xl text-[#18231E] mb-1" style={{ fontFamily: "Outfit" }}>Who needs a care team?</h2>
              <p className="text-sm text-[#586960] mb-6">Select an unmatched client to begin building their therapy schedule.</p>

              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-[#586960] font-medium mb-1.5">Client</label>
                  <select
                    data-testid="match-client-select"
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    className="w-full h-11 px-4 rounded-lg border border-[#E3DFD5] bg-white text-sm text-[#18231E] focus:outline-none focus:ring-2 focus:ring-[#274f38]/20 focus:border-[#274f38] transition-all"
                  >
                    <option value="">— choose a client —</option>
                    {clients.filter(c => !c.assigned_therapist_ids?.length).map(c => (
                      <option key={c.id} value={c.id}>{c.name} · needs {c.needed_hours_per_week} hr/wk · {c.age_group}</option>
                    ))}
                  </select>
                </div>
                <Button
                  data-testid="start-matching-btn"
                  onClick={startMatching}
                  disabled={!selectedClientId}
                  className="h-11 px-6 bg-[#274f38] hover:bg-[#1E3D2B] text-white rounded-lg font-medium text-sm transition-colors"
                >
                  <Sparkles size={15} className="mr-2" strokeWidth={1.5} /> Start matching
                </Button>
              </div>

              {(() => {
                const n = clients.filter(c => c.assigned_therapist_ids?.length).length;
                return n > 0 ? (
                  <p className="mt-3 text-xs text-[#A1ACA6]">
                    {n} client{n !== 1 ? "s" : ""} already matched — manage them on the Schedule page.
                  </p>
                ) : null;
              })()}
            </div>

            {/* Discipline info grid */}
            <div className="grid grid-cols-3 gap-4">
              {DISCIPLINES.map((d, i) => (
                <div key={d.key} className="bg-white border border-[#E3DFD5] rounded-xl p-5 shadow-[0_2px_8px_rgba(24,35,30,0.04)]">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: palette[i] }}>
                      <d.icon size={18} strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-[#18231E]" style={{ fontFamily: "Outfit" }}>{d.long}</div>
                      <div className="text-[11px] text-[#A1ACA6] font-mono">{d.defaultHours} hr/wk default</div>
                    </div>
                  </div>
                  <p className="text-[12px] text-[#586960] leading-relaxed">{d.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---- STEPS 1-3: discipline matching ---- */}
        {step >= 1 && step <= 3 && client && (
          <DisciplineStep
            stepIdx={step}
            disciplineKey={["bt", "program_manager", "bcba"][step - 1]}
            client={client}
            teamHours={teamHours}
            matches={matches}
            picked={picked}
            allBlocks={allBlocks}
            allExisting={allExisting}
            allTherapists={allTherapists}
            loadingDisc={loadingDisc}
            dailyHours={dailyHours}
            onDailyHoursChange={setDailyHours}
            onRunBTMatch={runBTMatch}
            pmDailyHours={pmDailyHours}
            onPmDailyHoursChange={setPmDailyHours}
            onRunPMMatch={runPMMatch}
            bcbaDays={bcbaDays}
            onBcbaDaysChange={(days) => { setBcbaDays(days); runBCBAMatch(days); }}
            onPick={pickOption}
            onBack={() => advanceTo(step - 1)}
            onNext={() => advanceTo(step + 1)}
            onSkip={() => advanceTo(step + 1)}
          />
        )}

        {/* ---- STEP 4: review & confirm ---- */}
        {step === 4 && (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-5" data-testid="step-review">
            <div className="space-y-4">
              {/* Team summary */}
              <div className="bg-white border border-[#E3DFD5] rounded-xl p-6 shadow-[0_2px_8px_rgba(24,35,30,0.04)]">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1ACA6] font-bold mb-1">Step 5 of 5</div>
                <h2 className="text-2xl text-[#18231E] mb-1" style={{ fontFamily: "Outfit" }}>Confirm care team</h2>
                <p className="text-sm text-[#586960] mb-5">for <span className="font-medium text-[#18231E]">{client?.name}</span></p>

                <div className="space-y-2.5">
                  {DISCIPLINES.map((d, i) => {
                    const opt = picked[d.key];
                    return (
                      <div
                        key={d.key}
                        data-testid={`review-${d.key}`}
                        className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${
                          opt ? "border-[#274f38]/20 bg-[#F7FAF8]" : "border-[#E3DFD5] bg-[#FAFAF9]"
                        }`}
                      >
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: palette[i] }}>
                          <d.icon size={17} strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1ACA6] font-bold">{d.long}</div>
                          {opt ? (
                            <>
                              <div className="font-semibold text-[#18231E] text-sm" style={{ fontFamily: "Outfit" }}>
                                {opt.therapists[0].therapist_name}
                              </div>
                              <div className="text-[11px] text-[#586960] font-mono mt-0.5">
                                {opt.coverage_hours.toFixed(2)} / {opt.needed_hours} hr per week
                                {opt.gap_hours > 0 && <span className="text-[#B07C60] ml-1.5">· {opt.gap_hours.toFixed(2)} hr gap</span>}
                              </div>
                            </>
                          ) : (
                            <div className="text-sm text-[#A1ACA6] italic">— skipped —</div>
                          )}
                        </div>
                        {opt
                          ? <CheckCircle2 size={18} className="text-[#274f38] shrink-0" />
                          : <span className="text-[10px] text-[#A1ACA6] shrink-0 uppercase tracking-[0.12em] font-semibold">optional</span>
                        }
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recurrence */}
              <div className="bg-white border border-[#E3DFD5] rounded-xl p-6 shadow-[0_2px_8px_rgba(24,35,30,0.04)] space-y-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1ACA6] font-bold">Recurrence</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-[#586960] font-medium mb-1.5">Start the week of</label>
                    <Input
                      type="date"
                      data-testid="confirm-week-start"
                      value={weekStart}
                      onChange={(e) => setWeekStart(e.target.value)}
                      className="font-mono text-sm border-[#E3DFD5] focus:border-[#274f38] focus:ring-[#274f38]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#586960] font-medium mb-1.5">Recurring weeks</label>
                    <Input
                      type="number"
                      min={1}
                      max={104}
                      data-testid="confirm-recurring-weeks"
                      value={recurringWeeks}
                      onChange={(e) => setRecurringWeeks(parseInt(e.target.value) || 1)}
                      className="font-mono text-sm border-[#E3DFD5] focus:border-[#274f38] focus:ring-[#274f38]"
                    />
                  </div>
                </div>
                <p className="text-xs text-[#A1ACA6] leading-relaxed">
                  Sessions repeat weekly. Default is 26 weeks (~6 months). You can cancel any series from the Schedule page.
                </p>

                <Button
                  data-testid="confirm-team-btn"
                  onClick={confirmTeam}
                  disabled={confirming || !picked.bt}
                  className="w-full h-11 bg-[#274f38] hover:bg-[#1E3D2B] text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                >
                  <CalendarCheck size={16} className="mr-2" strokeWidth={1.5} />
                  {confirming ? "Scheduling…" : "Confirm & schedule recurring team"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStep(3)}
                  className="w-full border-[#E3DFD5] text-[#18231E] hover:border-[#274f38] hover:text-[#274f38] rounded-lg"
                >
                  <ArrowLeft size={14} className="mr-1.5" strokeWidth={1.5} /> Back to BCBA step
                </Button>
              </div>
            </div>

            {/* Right: combined weekly preview */}
            <div className="bg-white border border-[#E3DFD5] rounded-xl p-6 shadow-[0_2px_8px_rgba(24,35,30,0.04)] lg:sticky lg:top-6 self-start">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1ACA6] font-bold mb-1">Combined weekly schedule</div>
              <h3 className="text-xl text-[#18231E] mb-4" style={{ fontFamily: "Outfit" }}>
                {allBlocks.length} blocks ·{" "}
                <span className="font-mono text-lg">
                  {allBlocks.reduce((s, b) => {
                    const [sh, sm] = b.start.split(":").map(Number);
                    const [eh, em] = b.end.split(":").map(Number);
                    return s + ((eh * 60 + em) - (sh * 60 + sm)) / 60;
                  }, 0).toFixed(1)} hr/wk
                </span>
              </h3>
              <div className="mb-4 flex flex-wrap gap-2.5">
                {allTherapists.map((t, i) => (
                  <span key={t.therapist_id} className="inline-flex items-center gap-1.5 text-xs text-[#586960]">
                    <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: palette[i % palette.length] }} />
                    {t.therapist_name}
                    <span className="text-[#A1ACA6]">({DISCIPLINES.find(d => d.key === t.discipline)?.label})</span>
                  </span>
                ))}
              </div>
              <ReviewDayGrid blocks={allBlocks} therapists={allTherapists} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Review day grid ----
const REVIEW_DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const REVIEW_ROLE_LABEL = { bt: "BT", program_manager: "PM", bcba: "BCBA" };
const REVIEW_COLORS = [
  { bg: "#274f38", border: "#1E3D2B", pill: "#E8F0EA", text: "#274f38" },
  { bg: "#B07C60", border: "#946750", pill: "#FAF0E8", text: "#7a4e32" },
  { bg: "#5b7fa6", border: "#3d6080", pill: "#E8EFF5", text: "#3d6080" },
];

function ReviewDayGrid({ blocks = [], therapists = [] }) {
  const colorMap = {};
  therapists.forEach((t, i) => { colorMap[t.therapist_id] = REVIEW_COLORS[i % REVIEW_COLORS.length]; });

  const byDay = {};
  for (let i = 0; i < 7; i++) byDay[i] = [];
  blocks.forEach(b => byDay[b.day]?.push(b));
  const activeDays = [0, 1, 2, 3, 4, 5, 6].filter(d => byDay[d].length > 0);

  if (!activeDays.length) return <p className="text-sm text-[#A1ACA6]">No blocks selected yet.</p>;

  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(activeDays.length, 5)}, minmax(0,1fr))` }}>
      {activeDays.map(day => (
        <div key={day} className="rounded-lg border border-[#E3DFD5] overflow-hidden bg-white">
          <div className="px-2.5 py-2 bg-[#F5F3EE] border-b border-[#E3DFD5] text-[10px] font-bold uppercase tracking-[0.18em] text-[#586960] text-center">
            {REVIEW_DAY_LABELS[day]}
          </div>
          <div className="p-2 space-y-1.5">
            {byDay[day].sort((a, b) => a.start.localeCompare(b.start)).map((b, i) => {
              const c = colorMap[b.therapist_id] || REVIEW_COLORS[0];
              const role = therapists.find(t => t.therapist_id === b.therapist_id)?.discipline;
              return (
                <div key={i} style={{ borderLeft: `3px solid ${c.border}`, backgroundColor: c.pill }} className="rounded-md px-2 py-1.5">
                  <div style={{ color: c.text }} className="text-[9px] font-black uppercase tracking-widest leading-none mb-0.5">
                    {REVIEW_ROLE_LABEL[role] || role || ""}
                  </div>
                  <div className="text-[12px] font-semibold text-[#18231E] leading-tight truncate">
                    {b.therapist_name}
                  </div>
                  <div className="text-[11px] font-mono text-[#586960] leading-tight mt-0.5">
                    {b.start}–{b.end}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Discipline step ----
function DisciplineStep({
  stepIdx, disciplineKey, client, teamHours, matches, picked,
  loadingDisc,
  dailyHours, onDailyHoursChange, onRunBTMatch,
  pmDailyHours, onPmDailyHoursChange, onRunPMMatch,
  bcbaDays, onBcbaDaysChange,
  onPick, onBack, onNext, onSkip,
}) {
  const d = DISCIPLINES.find(x => x.key === disciplineKey);
  const result = matches[disciplineKey];
  const target = teamHours[disciplineKey];
  const selected = picked[disciplineKey];
  const isLoading = loadingDisc === disciplineKey;
  const isBT = disciplineKey === "bt";
  const isPM = disciplineKey === "program_manager";
  const isBCBA = disciplineKey === "bcba";
  const opts = result?.single_options || [];
  const noOptions = !isLoading && result && opts.length === 0;
  const colorIdx = DISCIPLINES.findIndex(x => x.key === disciplineKey);
  const accentColor = palette[colorIdx];

  const dailyTotal = Object.values(dailyHours).reduce((s, h) => s + (parseFloat(h) || 0), 0);
  const pmTotal = Object.values(pmDailyHours).reduce((s, h) => s + (parseFloat(h) || 0), 0);

  const btDays = picked.bt ? [...new Set(picked.bt.proposed_blocks.map(b => b.day))].sort() : [];

  const [hovered, setHovered] = useState(null);
  const previewBlocks = useMemo(() => {
    const blocks = [];
    DISCIPLINES.forEach((dd) => {
      const opt = dd.key === disciplineKey ? (selected || hovered) : picked[dd.key];
      if (!opt) return;
      opt.proposed_blocks.forEach(b => blocks.push(b));
    });
    return blocks;
  }, [picked, selected, hovered, disciplineKey]);

  const previewTherapists = useMemo(() => {
    const out = [];
    DISCIPLINES.forEach((dd) => {
      const opt = dd.key === disciplineKey ? (selected || hovered) : picked[dd.key];
      if (!opt) return;
      out.push({ ...opt.therapists[0], discipline: dd.key });
    });
    return out;
  }, [picked, selected, hovered, disciplineKey]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-5" data-testid={`step-${disciplineKey}`}>
      {/* Left: options */}
      <div className="space-y-3">
        {/* Step header */}
        <div className="bg-white border border-[#E3DFD5] rounded-xl p-5 shadow-[0_2px_8px_rgba(24,35,30,0.04)]">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: accentColor }}>
                <d.icon size={17} strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1ACA6] font-bold">Step {stepIdx + 1} of 5</div>
                <h2 className="text-xl text-[#18231E] leading-tight" style={{ fontFamily: "Outfit" }}>Pick a {d.label}</h2>
              </div>
            </div>
            {!isBT && (
              <span className="text-[10px] px-2.5 py-1 rounded-full bg-[#FDF4E7] text-[#B07C60] uppercase tracking-[0.12em] font-semibold shrink-0">
                Joins BT sessions
              </span>
            )}
          </div>
          <p className="text-sm text-[#586960] mt-1">
            Need <span className="font-mono font-medium text-[#18231E]">{target} hr/wk</span>.{" "}
            {!isBT && "Available windows are constrained to overlap the BT's proposed schedule."}
          </p>
          {/* Team-so-far chips */}
          {(picked.bt || picked.program_manager || picked.bcba) && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#F0EFEA]">
              {DISCIPLINES.filter(dd => dd.key !== disciplineKey && picked[dd.key]).map(dd => (
                <DisciplineChip key={dd.key} discipline={dd.key} picked={picked} />
              ))}
            </div>
          )}
        </div>

        {/* BT daily hours editor */}
        {isBT && (
          <div className="bg-white border border-[#E3DFD5] rounded-xl p-5 shadow-[0_2px_8px_rgba(24,35,30,0.04)]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1ACA6] font-bold mb-1">Daily schedule</div>
            <p className="text-sm text-[#586960] mb-4">
              Adjust hours per day, then find matches. Default is 2 hr/day Mon–Fri.
            </p>
            <div className="grid grid-cols-5 gap-2 mb-4">
              {DAY_LABELS.slice(0, 5).map((label, day) => (
                <div key={day} className="flex flex-col items-center gap-1.5">
                  <span className="text-[11px] font-semibold text-[#586960] uppercase tracking-[0.12em]">{label}</span>
                  <input
                    type="number" min={0} max={8} step={0.5}
                    value={dailyHours[day] ?? 0}
                    onChange={(e) => {
                      const val = Math.max(0, parseFloat(e.target.value) || 0);
                      const next = { ...dailyHours };
                      if (val === 0) delete next[day]; else next[day] = val;
                      onDailyHoursChange(next);
                    }}
                    className="w-full text-center h-10 rounded-lg border border-[#E3DFD5] bg-white text-sm font-mono text-[#18231E] focus:outline-none focus:ring-2 focus:ring-[#274f38]/20 focus:border-[#274f38] transition-all"
                  />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#586960]">
                Total: <span className="font-mono font-semibold text-[#274f38]">{dailyTotal.toFixed(1)} hr/wk</span>
              </span>
              <button
                type="button" onClick={onRunBTMatch}
                disabled={isLoading || dailyTotal === 0}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#274f38] hover:bg-[#1E3D2B] text-white text-sm font-medium disabled:opacity-50 transition-colors"
              >
                <Sparkles size={14} strokeWidth={1.5} />
                {isLoading ? "Finding…" : result ? "Re-run" : "Find matches"}
              </button>
            </div>
          </div>
        )}

        {/* PM day editor */}
        {isPM && (
          <div className="bg-white border border-[#E3DFD5] rounded-xl p-5 shadow-[0_2px_8px_rgba(24,35,30,0.04)]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1ACA6] font-bold mb-1">Weekly visit day</div>
            <p className="text-sm text-[#586960] mb-1">
              PM sees the client for <span className="font-medium text-[#18231E]">2 hr/week</span>. Only days with BT sessions are shown.
            </p>
            {btDays.length === 0 ? (
              <p className="text-xs text-[#B07C60] mt-2">No BT days found — go back and pick a BT first.</p>
            ) : (
              <div className="flex flex-wrap gap-2 mt-3 mb-4">
                {btDays.map((day) => (
                  <div key={day} className="flex flex-col items-center gap-1.5 min-w-[52px]">
                    <span className="text-[11px] font-semibold text-[#586960] uppercase tracking-[0.12em]">{DAY_LABELS[day]}</span>
                    <input
                      type="number" min={0} max={2} step={0.5}
                      value={pmDailyHours[day] ?? 0}
                      onChange={(e) => {
                        const val = Math.max(0, Math.min(2, parseFloat(e.target.value) || 0));
                        const next = { ...pmDailyHours };
                        if (val === 0) delete next[day]; else next[day] = val;
                        onPmDailyHoursChange(next);
                      }}
                      className="w-full text-center h-10 rounded-lg border border-[#E3DFD5] bg-white text-sm font-mono text-[#18231E] focus:outline-none focus:ring-2 focus:ring-[#274f38]/20 focus:border-[#274f38] transition-all"
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#586960]">
                Total:{" "}
                <span className={`font-mono font-semibold ${pmTotal > 2 ? "text-[#B07C60]" : "text-[#274f38]"}`}>
                  {pmTotal.toFixed(1)} / 2 hr/wk
                </span>
                {pmTotal > 2 && <span className="ml-1.5 text-[#B07C60] text-[11px]">· exceeds limit</span>}
              </span>
              <button
                type="button" onClick={onRunPMMatch}
                disabled={isLoading || pmTotal === 0 || pmTotal > 2}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#274f38] hover:bg-[#1E3D2B] text-white text-sm font-medium disabled:opacity-50 transition-colors"
              >
                <Sparkles size={14} strokeWidth={1.5} />
                {isLoading ? "Finding…" : result ? "Re-run" : "Find matches"}
              </button>
            </div>
          </div>
        )}

        {/* BCBA day picker */}
        {isBCBA && (
          <div className="bg-white border border-[#E3DFD5] rounded-xl p-5 shadow-[0_2px_8px_rgba(24,35,30,0.04)]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1ACA6] font-bold mb-1">Monthly visit days</div>
            <p className="text-sm text-[#586960] mb-1">
              BCBA sees the client <span className="font-medium text-[#18231E]">3 sessions/month · 1 hr each</span>. Pick up to 3 BT session days.
            </p>
            <p className="text-xs text-[#A1ACA6] mb-4">Changing selection re-runs the match automatically.</p>
            {btDays.length === 0 ? (
              <p className="text-xs text-[#B07C60]">No BT days found — go back and pick a BT first.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {btDays.map((day) => {
                  const checked = bcbaDays.includes(day);
                  const atLimit = !checked && bcbaDays.length >= 3;
                  return (
                    <button
                      key={day} type="button" disabled={atLimit}
                      onClick={() => {
                        const next = checked
                          ? bcbaDays.filter(d => d !== day)
                          : [...bcbaDays, day].sort();
                        onBcbaDaysChange(next);
                      }}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                        checked
                          ? "bg-[#274f38] text-white border-[#274f38] shadow-[0_2px_8px_rgba(39,79,56,0.2)]"
                          : atLimit
                          ? "bg-[#F9F8F5] text-[#A1ACA6] border-[#E3DFD5] opacity-40 cursor-not-allowed"
                          : "bg-white text-[#274f38] border-[#E3DFD5] hover:border-[#274f38]"
                      }`}
                    >
                      {DAY_LABELS[day]}
                    </button>
                  );
                })}
              </div>
            )}
            {bcbaDays.length > 0 && (
              <p className="text-xs text-[#586960] mt-3">
                <span className="font-mono font-semibold text-[#274f38]">{bcbaDays.length} day{bcbaDays.length !== 1 ? "s" : ""} selected</span>
                {" · "}3 sessions/month on the first {bcbaDays.map(d => DAY_LABELS[d]).join(", ")} of each month
              </p>
            )}
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center gap-3 px-5 py-4 bg-white border border-[#E3DFD5] rounded-xl" data-testid="step-loading">
            <div className="w-4 h-4 rounded-full border-2 border-[#274f38] border-t-transparent animate-spin" />
            <span className="text-sm text-[#586960]">Finding the best matches…</span>
          </div>
        )}

        {/* No results */}
        {noOptions && (
          <div className="bg-[#FDF4E7] border border-[#E3C68B] rounded-xl p-5 text-sm text-[#7A5B2E]" data-testid="step-no-options">
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold mb-0.5">No {d.label} matches found</div>
                <p className="text-[13px] leading-relaxed">
                  {disciplineKey !== "bt"
                    ? `Make sure at least one therapist is tagged as "${d.long}" with availability that overlaps the BT's proposed blocks.`
                    : `Add a BT therapist with availability that overlaps the client's window.`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Option cards */}
        {opts.map((o, i) => (
          <div key={i} onMouseEnter={() => setHovered(o)} onMouseLeave={() => setHovered(null)}>
            <OptionCard
              option={o} idx={i} color={accentColor}
              isSelected={selected === o}
              onSelect={() => onPick(disciplineKey, selected === o ? null : o)}
              testId={`option-${disciplineKey}-${i}`}
            />
          </div>
        ))}

        {/* Navigation */}
        <div className="flex items-center justify-between gap-3 pt-2">
          <Button
            variant="outline"
            onClick={onBack}
            data-testid="step-back"
            className="border-[#E3DFD5] text-[#18231E] hover:border-[#274f38] hover:text-[#274f38] rounded-lg"
          >
            <ArrowLeft size={14} className="mr-1.5" strokeWidth={1.5} /> Back
          </Button>
          <div className="flex gap-2">
            {disciplineKey !== "bt" && (
              <Button
                variant="outline"
                onClick={onSkip}
                data-testid="step-skip"
                className="border-[#E3DFD5] text-[#586960] hover:border-[#274f38] hover:text-[#274f38] rounded-lg"
              >
                Skip {d.label}
              </Button>
            )}
            <Button
              onClick={onNext}
              disabled={disciplineKey === "bt" && !selected}
              data-testid="step-next"
              className="bg-[#274f38] hover:bg-[#1E3D2B] text-white rounded-lg px-5 font-medium text-sm disabled:opacity-50 transition-colors"
            >
              {selected ? `Continue with ${selected.therapists[0].therapist_name.split(" ")[0]}` : "Continue"}
              <ArrowRight size={14} className="ml-1.5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </div>

      {/* Right: live weekly preview */}
      <div className="bg-white border border-[#E3DFD5] rounded-xl p-6 shadow-[0_2px_8px_rgba(24,35,30,0.04)] lg:sticky lg:top-6 self-start">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1ACA6] font-bold mb-1">Live preview</div>
        <h3 className="text-xl text-[#18231E] mb-1" style={{ fontFamily: "Outfit" }}>Team week so far</h3>
        <p className="text-xs text-[#A1ACA6] mb-4">Hover an option to preview it · click to select.</p>
        <div className="mb-4 flex flex-wrap gap-3">
          {previewTherapists.map((t, i) => (
            <span key={t.therapist_id} className="inline-flex items-center gap-1.5 text-xs text-[#586960]">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: palette[i % palette.length] }} />
              {t.therapist_name}
              <span className="text-[#A1ACA6]">({DISCIPLINES.find(dd => dd.key === t.discipline)?.label})</span>
            </span>
          ))}
          {previewTherapists.length === 0 && (
            <span className="text-xs text-[#A1ACA6] italic">No selections yet — hover or pick an option.</span>
          )}
        </div>
        <WeeklyPreview blocks={previewBlocks} existingBlocks={[]} therapists={previewTherapists} />
        <div className="mt-4 pt-4 border-t border-[#F0EFEA] text-xs text-[#A1ACA6] inline-flex items-center gap-1.5">
          <MapPin size={12} strokeWidth={1.5} /> {client.home_address}
        </div>
      </div>
    </div>
  );
}
